import React, { useState, useEffect } from 'react'
import { mlbLeagues, mlbTeams } from '../../../../../utils/mlbUtils';
import { nflConferences, nflTeams } from '../../../../../utils/nflUtils';
import { nbaConferences, nbaTeams } from '../../../../../utils/nbaUtils';
import Select from 'react-select';

const TeamSelector = (props) => {
    const [critVal, setCritVal] = useState({
        teams: [],
        label: "",
        type: "",
        homeAway: ""
    });
    const [err, setErr] = useState(false);
    const [initTeams, setInitTeams] = useState([]);
    const [critIdx, setCritIdx] = useState(null);
    const [groupedOptions, setGroupedOptions] = useState([]);
    const [sportInfo, setSportInfo]= useState(null)
    const [teams, setTeams] = useState([]);

    // run when sysContext changes
    useEffect(()=>{
        const sport = props.sysContext.sport;
        if(sport === "mlb"){
            setSportInfo({
                teams: mlbTeams,
                groups: mlbLeagues
            });
        }else if(sport === "nfl"){
            setSportInfo({
                teams: nflTeams,
                groups: nflConferences
            });
        }else if(sport === "nba"){
            setSportInfo({
                teams: nbaTeams,
                groups: nbaConferences
            });
        };
    },[props.sysContext]);

    // run when sport info changes - create option groups for select
    useEffect(()=>{
        if(sportInfo){
            let tmpGroups = [];
            for(const group in sportInfo.groups){

                if(props.sysContext.sport == "nba"){
                    const options = sportInfo.groups[group].map(team => ({
                        value: team, 
                        label: sportInfo.teams[team]
                    }));

                    tmpGroups.push({
                        label: `${group}`,
                        options: options
                    });
                }else{
                    for(const subGroup in sportInfo.groups[group]){
                        const options = sportInfo.groups[group][subGroup].map(team => ({
                            value: team, 
                            label: sportInfo.teams[team]
                        }));
    
                        tmpGroups.push({
                            label: `${group} ${subGroup}`,
                            options: options
                        });
                    };
                };
            };
            setGroupedOptions(tmpGroups);
        };
    },[sportInfo]);

    // run when init crit changes
    useEffect(()=> {
        // check if user is editing an existing criteria
        if(props.initCrit){
            const critCopy = {...props.initCrit.crit};
            setCritVal(critCopy);
            setTeams(critCopy.teams);
            setInitTeams(critCopy.teams);
            setCritIdx(props.initCrit.index);
        };
    },[props.initCrit]);

    // set submit function in parent component
    const validateSubmit = (e) => {
        e.preventDefault();
        // make sure criteria is not null
        if(critVal.teams.length > 0){
            if(critIdx !== null){
                const sameLength = critVal.teams.length === initTeams.length
                const sameTeams = critVal.teams.every(element => initTeams.includes(element));
                if(!sameLength || !sameTeams){
                    props.sysContext.changeCrit(critVal, critIdx);
                }
            }else{
                props.sysContext.changeCrit(critVal);
            };
            props.sysContext.setStep(2);
        }else{
            setErr(true);
        };
    };

    // Function to generate criteria label
    const generateLabel = () => {
        let label = "";
        const homeAwayLbl = props.homeAway === "home" ? "Home team" : "Away team";
        if(teams.length === 1){
            label = `${homeAwayLbl} is ${sportInfo.teams[teams[0]]}`;
        }else if(teams.length > 1){
            label = `${homeAwayLbl} is one of `;
            const lastIdx = teams.length-1;
            teams.forEach((team, i) => {
                const long = sportInfo.teams[team];
                if(i !== lastIdx){
                    label = label+long+", ";
                }else{
                    label = label+long;
                };
            });
        };

        return label;
    };

    // run when teams or homeAway prop changes - update crit
    useEffect(()=>{
        const tmpCrit = {...critVal};
        tmpCrit.homeAway = props.homeAway;
        tmpCrit.type = `team.${props.homeAway}-team-sel`;
        tmpCrit.teams = teams;
        const label = generateLabel();
        tmpCrit.label = label;
        setCritVal(tmpCrit);
    },[teams, props.homeAway]);

    const handleChange = (sel) => {
        const values = sel ? sel.map(opt => opt.value) : [];
        setTeams(values);
        // Only clear error if there was one and we now have a selection
        if (err && values.length > 0) {
            setErr(false);
        }
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: 'white',
            borderColor: err ? 'rgb(239, 68, 68)' : 'rgb(209, 213, 219)',
            borderWidth: err ? '2px' : '1px',
            boxShadow: err ? '0 0 0 2px rgb(239 68 68 / 0.5)' : state.isFocused ? '0 0 0 2px rgb(156 163 175 / 0.5)' : 'none',
            '&:hover': {
                borderColor: err ? 'rgb(239, 68, 68)' : 'rgb(156, 163, 175)',
            },
            borderRadius: '0.375rem',
            padding: '2px',
            transition: 'all 150ms ease',
            '.dark &': {
                backgroundColor: 'rgb(31, 41, 55)',
                borderColor: err ? 'rgb(239, 68, 68)' : 'rgb(75, 85, 99)',
            },
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: 'rgb(255, 255, 255)',
            border: '1px solid rgb(209, 213, 219)',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            '.dark &': {
                backgroundColor: 'rgb(31, 41, 55)',
                border: '1px solid rgb(75, 85, 99)',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? 'rgb(243, 244, 246)' 
                : state.isFocused 
                    ? 'rgb(249, 250, 251)'
                    : 'transparent',
            color: state.isSelected ? 'rgb(17, 24, 39)' : 'rgb(17, 24, 39)',
            padding: '8px 12px',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: 'rgb(249, 250, 251)',
            },
            '.dark &': {
                backgroundColor: state.isSelected 
                    ? 'rgb(55, 65, 81)' 
                    : state.isFocused 
                        ? 'rgb(55, 65, 81)'
                        : 'transparent',
                color: state.isSelected ? 'rgb(209, 213, 219)' : 'rgb(209, 213, 219)',
                '&:hover': {
                    backgroundColor: 'rgb(55, 65, 81)',
                },
            },
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'rgb(243, 244, 246)',
            borderRadius: '0.375rem',
            padding: '1px',
            '.dark &': {
                backgroundColor: 'rgb(55, 65, 81)',
            },
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'rgb(17, 24, 39)',
            padding: '2px 6px',
            fontSize: '0.875rem',
            '.dark &': {
                color: 'rgb(209, 213, 219)',
            },
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: 'rgb(107, 114, 128)',
            padding: '0 4px',
            cursor: 'pointer',
            borderRadius: '0 0.375rem 0.375rem 0',
            '&:hover': {
                backgroundColor: 'rgb(229, 231, 235)',
                color: 'rgb(17, 24, 39)',
            },
            '.dark &': {
                color: 'rgb(156, 163, 175)',
                '&:hover': {
                    backgroundColor: 'rgb(75, 85, 99)',
                    color: 'rgb(209, 213, 219)',
                },
            },
        }),
        input: (provided) => ({
            ...provided,
            color: 'rgb(17, 24, 39)',
            '.dark &': {
                color: 'rgb(209, 213, 219)',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'rgb(107, 114, 128)',
            '.dark &': {
                color: 'rgb(156, 163, 175)',
            },
        }),
        groupHeading: (provided) => ({
            ...provided,
            color: 'rgb(107, 114, 128)',
            fontSize: '0.875rem',
            fontWeight: '600',
            textTransform: 'none',
            padding: '8px 12px',
            '.dark &': {
                color: 'rgb(156, 163, 175)',
            },
        }),
    };

    return (
        <div className="space-y-6">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                <h2 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">
                    {props.homeAway === "home" ? "Home" : "Away"} Team Selector
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Select one or more teams to include in your criteria.
                </p>
            </div>

            <form className="space-y-6" onSubmit={validateSubmit}>
                <div className="space-y-2">
                    <label htmlFor="team-select" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Select Teams
                    </label>
                    <Select 
                        id="team-select"
                        isMulti
                        options={groupedOptions}
                        styles={customSelectStyles}
                        value={groupedOptions.reduce((acc, group) => {
                            acc.push(...group.options.filter(option => teams.includes(option.value)));
                            return acc;
                        }, [])}
                        onChange={handleChange}
                        closeMenuOnSelect={false}
                        placeholder="Select teams..."
                        className={`w-full`}
                    />
                    {err && (
                        <p className="mt-2 text-sm text-brand-red-500 dark:text-brand-red-400">
                            Please make a selection, or click cancel to go back.
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => props.cancel()}
                        className="rounded-md bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                    >
                        Save Criteria
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TeamSelector;